// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Select from "@gcg/MobiusOverrides/Select/Select";
import TextField from "@gcg/MobiusOverrides/TextField";
import { screen } from "@gcg/XDL/Helpers";
import { color } from "@gcg/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Search from "@insite/mobius/Icons/Search";
import { GridContainer, GridItem, Hidden } from "@insite/mobius/index";
import Link, { LinkProps } from "@insite/mobius/Link";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { css } from "styled-components";

type Props = {
    alphabet: LetterDetails[];
    prefix?: string;
    extendedStyles?: AlphabetNavigationStyles;
    onLetterClick?: (letter: string) => void;
    hideFilterBrands?: boolean;
    filterBrands?: string;
    handleFilterBrands?: (e: any) => void;
    selectBrandLetter?: string;
    handleSelectBrandLetterChange?: (e: any) => void;
};

export interface LetterDetails {
    letter: string;
    linkable: boolean;
}

export interface AlphabetNavigationStyles {
    container?: InjectableCss;
    containerWithHideFilterBrands?: InjectableCss;
    containerWithoutHideFilterBrands?: InjectableCss;
    navigationItem?: InjectableCss;
    navigationLink?: LinkProps;
    navigationTextEmpty?: TypographyPresentationProps;
    filterBrandsTitle?: any;
    filterBrandsGridItem?: GridItemProps;
    alphabetGridItemWithHideFilterBrands?: GridItemProps;
    alphabetGridItemWithoutHideFilterBrands?: GridItemProps;
    hiddenNavigation?: any;
    hiddenSelect?: any;
    gridContainer?: GridContainerProps;
    selectStyle?: SelectPresentationProps;
    filterBrands?: TextFieldProps;
}

const sharedContainerCss = css`
    text-transform: uppercase;
    justify-items: center;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    background: ${getColor("common.ui2")};
    margin-bottom: 64px;
`;

export const alphabetNavigationStyles: AlphabetNavigationStyles = {
    containerWithHideFilterBrands: {
        css: css`
            padding: 21.5px 30px;
            ${sharedContainerCss}
        `,
    },
    containerWithoutHideFilterBrands: {
        css: css`
            padding: 17.5px 16px;
            ${sharedContainerCss}
        `,
    },
    navigationItem: {
        css: css`
            margin: 0;
            display: inline-block;
        `,
    },
    navigationLink: {
        css: css`
            display: block;
            letter-spacing: 2px;
            padding: 0;
            padding-right: 14px;
            font-weight: 600;
            &,
             &: focus {
                outline: 0;
            }
        `,
        typographyProps: {
            transform: "uppercase",
            variant: "headerTertiary",
            forwardAs: "span",
            size: 16,
            lineHeight: "26px",
        },
    },
    navigationTextEmpty: {
        transform: "uppercase",
        variant: "headerTertiary",
        forwardAs: "span",
        color: "common.disabled",
        size: 16,
        lineHeight: "26px",
        css: css`
            padding: 0;
            padding-right: 14px;
            font-weight: 600;
        `,
    },
    filterBrandsTitle: {
        weight: 600,
        size: 14,
        lineHeight: "17px",
        transform: "capitalize",
        css: css`
            flex: 0 0 auto;
            margin-right: 16px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            margin-right: 0;
                            width: 100%;
                            margin-bottom: 10px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    filterBrandsGridItem: {
        width: [12, 6, 6, 4, 4],
        css: css`
            align-items: center;
            ${screen("lte", "md")`
                padding: 20px 24px;
                padding-left:8px;
                align-items: flex-end;`}
            ${screen("lte", "xs")`
                padding-top: 0;
                padding-left:24px;`}
            > div > div {
                margin-top: 0 !important;
            }
        `,
    },
    alphabetGridItemWithHideFilterBrands: {
        css: css`
            padding: 20px 24px;
            align-items: flex-end;
        `,
    },
    alphabetGridItemWithoutHideFilterBrands: {
        css: css`
            padding: 27px 24px;
            align-items: center;
            ${screen("lte", "md")`padding: 20px 24px;
            padding-right:8px;`}
            ${screen("lte", "xs")`
            padding-right:24px;`}
        `,
    },
    hiddenNavigation: {
        below: "md",
        css: css``,
    },
    hiddenSelect: {
        above: "sm",
        css: css`
            width: 100%;
            label {
                text-transform: none;
                font-size: 14px;
                font-weight: 600;
                letter-spacing: 0;
                line-height: 16px;
            }
        `,
    },
    gridContainer: {
        offsetCss: css`
            justify-content: flex-end;
        `,
    },
    filterBrands: {
        cssOverrides: {
            formField: css`
                max-width: 430px;
                input::placeholder {
                    color: ${color("common.ui4")};
                    font-style: italic;
                }
            `,
        },
    },
};

const StyledSpan = getStyledWrapper("span");

const AlphabetNavigation: FC<Props> = (props: Props) => {
    const [styles] = useState(() => mergeToNew(alphabetNavigationStyles, props.extendedStyles));

    const handleClickOfLetter = (letter: string) => {
        props.onLetterClick?.(letter);
    };

    const createHandleClickOfLetter = (letter: string) => () => {
        const letterElement = document.getElementById(
            `${props.prefix || ""}letter-${letter === "#" ? "number" : letter}`,
        );
        if (letterElement) {
            const letterOffset = letterElement.offsetTop;
            if (letterOffset > 0) {
                window.scrollTo(0, letterOffset + 380);
            }
        }
        return handleClickOfLetter(letter);
    };

    if (props.alphabet.length <= 0) {
        return null;
    }

    return (
        <StyledWrapper
            {...(props.hideFilterBrands
                ? styles.containerWithHideFilterBrands
                : styles.containerWithoutHideFilterBrands)}
            data-test-selector="alphabetNavigation"
        >
            <GridContainer {...styles.gridContainer}>
                <GridItem
                    {...(props.hideFilterBrands
                        ? styles.alphabetGridItemWithHideFilterBrands
                        : styles.alphabetGridItemWithoutHideFilterBrands)}
                    width={[12, 6, 6, 8, 8]}
                >
                    <Hidden {...styles.hiddenNavigation}>
                        {props.alphabet.map((alphabetItem, i) => (
                            <React.Fragment key={alphabetItem.letter}>
                                <StyledSpan
                                    {...styles.navigationItem}
                                    data-test-selector="alphabetNavigationItemLetter"
                                >
                                    {alphabetItem.linkable ? (
                                        <Link
                                            {...styles.navigationLink}
                                            onClick={createHandleClickOfLetter(alphabetItem.letter)}
                                        >
                                            {alphabetItem.letter === "#" ? "0-9" : alphabetItem.letter}
                                        </Link>
                                    ) : (
                                        <Typography {...styles.navigationTextEmpty}>
                                            {alphabetItem.letter === "#" ? "0-9" : alphabetItem.letter}
                                        </Typography>
                                    )}
                                </StyledSpan>
                            </React.Fragment>
                        ))}
                    </Hidden>
                    <Hidden {...styles.hiddenSelect}>
                        <Select
                            value={props.selectBrandLetter}
                            onChange={props.handleSelectBrandLetterChange}
                            label={translate("Jump to Letter")}
                            {...styles.selectStyle}
                        >
                            <option value="-9">{translate("Select Option")}</option>
                            {props.alphabet.map(alphabetItem => {
                                if (!alphabetItem.linkable) {
                                    return;
                                }

                                return (
                                    <option key={alphabetItem.letter} value={alphabetItem.letter}>
                                        {alphabetItem.letter === "#" ? "0-9" : alphabetItem.letter.toUpperCase()}
                                    </option>
                                );
                            })}
                        </Select>
                    </Hidden>
                </GridItem>
                {!props.hideFilterBrands && (
                    <GridItem {...styles.filterBrandsGridItem}>
                        <TextField
                            {...styles.filterBrands}
                            name="filterBrands"
                            value={props.filterBrands}
                            iconProps={{
                                src: Search,
                                css: css`
                                    left: auto;
                                    &&& {
                                        right: -5px;
                                        padding: 0;
                                    }
                                    svg {
                                        height: 25px;
                                        width: 25px;
                                        color: ${getColor("primary.main")};
                                    }
                                `,
                            }}
                            placeholder={translate("Search Manufacturers")}
                            onChange={props.handleFilterBrands}
                        />
                    </GridItem>
                )}
            </GridContainer>
        </StyledWrapper>
    );
};

export default AlphabetNavigation;
